import React, { useState, useMemo } from 'react';
import { Calculator, TrendingUp, Clock, Users, ArrowRight, Sparkles } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Seo from '../components/Seo';
import Header from '../components/Header';
import Footer from '../components/Footer';
import PageHero from '../components/PageHero';
import { Slider } from '../components/ui/slider';
import { Button } from '../components/ui/button';

const brl = (n) => n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 });

const CalculadoraRoi = () => {
  const navigate = useNavigate();
  const [agents, setAgents] = useState([8]);
  const [conversations, setConversations] = useState([2500]);
  const [avgTicket, setAvgTicket] = useState([180]);
  const [salary, setSalary] = useState([3500]);

  const results = useMemo(() => {
    const a = agents[0];
    const c = conversations[0];
    const t = avgTicket[0];
    const s = salary[0];

    // Assumptions:
    // CoreAI resolves ~60% of repetitive interactions autonomously
    // Each agent handles 1.4x more with copilot suggestions
    const autoResolved = c * 0.6;
    const agentCostSaving = Math.round((s * 1.3) * Math.max(0, a - Math.ceil(a / 1.4)));
    // Conversion uplift: 25% improvement in speed leads to +8% conversion
    const extraRevenue = Math.round(autoResolved * 0.08 * t);
    const timeSaved = Math.round(autoResolved * 3 / 60); // 3 min per interaction / 60 = hours
    const totalMonthly = agentCostSaving + extraRevenue;
    const annual = totalMonthly * 12;
    // Business plan: R$ 490 for 3 users = ~R$ 163 per user/month
    const costPerUser = 163;
    const roi = Math.round(((annual - costPerUser * a * 12) / (costPerUser * a * 12)) * 100);

    return { autoResolved, agentCostSaving, extraRevenue, timeSaved, totalMonthly, annual, roi };
  }, [agents, conversations, avgTicket, salary]);

  const SliderRow = ({ label, value, onChange, min, max, step, format }) => (
    <div>
      <div className="flex items-center justify-between mb-3">
        <label className="text-sm font-semibold text-[#0a0e27]">{label}</label>
        <span className="text-lg font-black text-[#FF5B24]">{format ? format(value[0]) : value[0]}</span>
      </div>
      <Slider value={value} onValueChange={onChange} min={min} max={max} step={step} className="w-full" />
      <div className="flex justify-between text-[10px] text-slate-400 mt-1">
        <span>{format ? format(min) : min}</span>
        <span>{format ? format(max) : max}</span>
      </div>
    </div>
  );

  return (
    <>
      <Seo
        title="Calculadora de ROI"
        description="Descubra em tempo real quanto sua empresa pode economizar e faturar a mais com os agentes de IA da CoreDesk. Simule o ROI da sua operação agora."
        path="/calculadora-roi"
      />
      <Header />
      <PageHero
        eyebrow="Calculadora de ROI"
        title="Descubra o retorno |da CoreDesk"
        subtitle="Ajuste as métricas da sua operação abaixo e veja em tempo real o quanto você pode economizar e faturar a mais com a CoreDesk."
      />

      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-[1fr_1.1fr] gap-8">
            {/* Inputs */}
            <div className="bg-white rounded-3xl border border-slate-100 shadow-lg p-8">
              <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-widest text-[#FF5B24]">
                <Calculator size={13} /> Sua operação
              </div>
              <h2 className="mt-2 text-2xl font-extrabold text-[#0a0e27]">Conte para gente como é hoje</h2>
              <div className="mt-8 space-y-8">
                <SliderRow label="Quantidade de atendentes" value={agents} onChange={setAgents} min={1} max={50} step={1} />
                <SliderRow label="Conversas por mês" value={conversations} onChange={setConversations} min={100} max={20000} step={100} format={(v) => v.toLocaleString('pt-BR')} />
                <SliderRow label="Ticket médio de vendas" value={avgTicket} onChange={setAvgTicket} min={20} max={2000} step={10} format={brl} />
                <SliderRow label="Salário médio de um atendente" value={salary} onChange={setSalary} min={1500} max={10000} step={100} format={brl} />
              </div>
            </div>

            {/* Results */}
            <div className="space-y-4">
              <div className="rounded-3xl bg-gradient-to-br from-[#0a0e27] to-[#1a1f4d] p-8 text-white relative overflow-hidden">
                <div className="absolute -top-16 -right-16 w-64 h-64 bg-[#FF5B24]/25 rounded-full blur-3xl" />
                <div className="relative">
                  <div className="text-[11px] font-bold uppercase tracking-widest text-[#FF5B24]">Retorno estimado</div>
                  <div className="mt-2 text-xs text-white/60">Economia + receita adicional em 12 meses</div>
                  <div className="mt-4 text-5xl md:text-6xl font-black text-white">{brl(results.annual)}</div>
                  <div className="mt-4 inline-flex items-center gap-2 bg-white/10 border border-white/20 rounded-full px-3 py-1.5">
                    <Sparkles size={14} className="text-[#FF5B24]" />
                    <span className="text-sm font-bold text-white">ROI de {results.roi > 0 ? '+' : ''}{results.roi}%</span>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="bg-white rounded-2xl border border-slate-100 p-5">
                  <TrendingUp size={18} className="text-[#FF5B24]" />
                  <div className="mt-3 text-2xl font-black text-[#0a0e27]">{brl(results.extraRevenue)}</div>
                  <div className="text-xs text-slate-500 mt-1">Receita a mais por mês</div>
                </div>
                <div className="bg-white rounded-2xl border border-slate-100 p-5">
                  <Users size={18} className="text-[#FF5B24]" />
                  <div className="mt-3 text-2xl font-black text-[#0a0e27]">{brl(results.agentCostSaving)}</div>
                  <div className="text-xs text-slate-500 mt-1">Economia com equipe/mês</div>
                </div>
                <div className="bg-white rounded-2xl border border-slate-100 p-5">
                  <Clock size={18} className="text-[#FF5B24]" />
                  <div className="mt-3 text-2xl font-black text-[#0a0e27]">{results.timeSaved.toLocaleString('pt-BR')}h</div>
                  <div className="text-xs text-slate-500 mt-1">Horas economizadas/mês</div>
                </div>
                <div className="bg-white rounded-2xl border border-slate-100 p-5">
                  <Sparkles size={18} className="text-[#FF5B24]" />
                  <div className="mt-3 text-2xl font-black text-[#0a0e27]">{Math.round(results.autoResolved).toLocaleString('pt-BR')}</div>
                  <div className="text-xs text-slate-500 mt-1">Conversas resolvidas pelo CoreAI</div>
                </div>
              </div>

              <div className="bg-white rounded-2xl border border-slate-100 p-5">
                <p className="text-xs text-slate-500 leading-relaxed">
                  *Estimativas baseadas em benchmarks do mercado de atendimento. Resultados reais variam conforme o segmento, maturação do time e implementação.
                </p>
              </div>

              <Button onClick={() => navigate('/contato')} className="btn-coral w-full h-13 py-4 rounded-full text-base font-semibold gap-2">
                Quero um diagnóstico personalizado <ArrowRight size={17} />
              </Button>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
};

export default CalculadoraRoi;
